import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { MerchantUser } from 'api/merchantUsers'
import { Role } from 'api/roles'
import { STATUS_WARNING_COLOR } from 'constants/colors'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import compact from 'lodash/compact'
import { Fragment, useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import BodyCell from 'scenes/Reports/components/Table/BodyCell'
import BodyCellContainer from 'scenes/Reports/components/Table/BodyCellContainer'
import { useStyletron } from 'styletron-react'
import { generateId } from 'utilities/uniqueId'
import useCurrentMerchantUser from '../../hooks/useCurrentMerchantUser'
import { roleTitle } from './helpers'
import StatusBadge from './StatusBadge'

const t = buildTranslate('merchant_users.merchant_users_list.table')

type Props = {
  merchantUser: MerchantUser & { role?: string }
  allRoles: Role[]
  rolesLoaded: boolean
  rbacEnabled: boolean
  canManage: boolean
  onRemove: VoidFunction
}

const Row = ({
  merchantUser,
  allRoles,
  rolesLoaded,
  rbacEnabled,
  canManage,
  onRemove,
}: Props) => {
  const merchant = useCurrentMerchant()
  const currentMerchantUser = useCurrentMerchantUser()
  const [css] = useStyletron()
  const removeTooltipId = useRef(
    `remove-tooltip-${merchantUser.id}-${generateId()}`
  ).current
  const name =
    compact([merchantUser.first_name, merchantUser.last_name]).join(' ') ||
    merchantUser.email

  const hideActions = currentMerchantUser?.id !== merchantUser.id

  const rbacRoleIds = merchantUser.rbac_role_ids ?? []
  const rbacRoles = rbacRoleIds
    .map(id => allRoles.find(r => r.id === id))
    .filter((r): r is NonNullable<typeof r> => Boolean(r))

  return (
    <tr>
      <BodyCell
        isErrored={false}
        isLoading={false}
        values={[{ value: name }]}
      />
      {rbacEnabled && rbacRoles.length > 0 ? (
        <BodyCellContainer
          className={'text-left'}
          isErrored={false}
          isLoading={false}
        >
          {rbacRoles.map((role, i) => (
            <Fragment key={role.id}>
              {i > 0 && ', '}
              <Link
                to={`/merchant_users/roles/${role.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={css({
                  textDecoration: 'none',
                  ':hover': { textDecoration: 'underline' },
                })}
              >
                <Text.BodyText4 tag="span" color="grey90" className="m-0">
                  {role.display_name}
                </Text.BodyText4>
              </Link>
            </Fragment>
          ))}
        </BodyCellContainer>
      ) : (
        <BodyCellContainer
          className={'text-left'}
          isErrored={false}
          isLoading={false}
        >
          <Text.BodyText4
            tag="span"
            color={rbacEnabled ? 'grey50' : 'grey90'}
            className="m-0"
          >
            {roleTitle(merchantUser.role, merchant?.merchant_type === 'Mall')}
          </Text.BodyText4>
          {rbacEnabled && rolesLoaded && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`no-rbac-tooltip-${merchantUser.id}`}>
                  {t('no_rbac_roles')}
                </Tooltip>
              }
            >
              <i
                className={`fa fa-exclamation-triangle ml-xs ${css({
                  cursor: 'help',
                  color: STATUS_WARNING_COLOR,
                  fontSize: '14px',
                })}`}
                role="img"
                aria-label={t('no_rbac_roles')}
                tabIndex={0}
              />
            </OverlayTrigger>
          )}
        </BodyCellContainer>
      )}
      <BodyCellContainer
        className={'text-left'}
        isErrored={false}
        isLoading={false}
      >
        <StatusBadge pending={merchantUser.pending} />
      </BodyCellContainer>
      <BodyCellContainer
        className={'text-right'}
        isErrored={false}
        isLoading={false}
      >
        <Switch condition={hideActions}>
          <Link to={`/merchant_users/edit/${merchantUser.id}`}>
            <Text.BodyText4 tag="span" className="ml-xs mb-0" color="primary">
              {t('edit')}
            </Text.BodyText4>
          </Link>
        </Switch>
      </BodyCellContainer>
      <BodyCellContainer
        className={'text-right'}
        isErrored={false}
        isLoading={false}
      >
        <Switch condition={hideActions}>
          {canManage ? (
            <button
              type="button"
              onClick={onRemove}
              className={`btn btn-link p-0 ${css({
                textDecoration: 'none',
                ':hover': { textDecoration: 'none' },
              })}`}
              aria-label={`${t('remove')} ${name}`}
            >
              <Text.BodyText4 tag="span" className="ml-xs mb-0" color="primary">
                {t('remove')}
              </Text.BodyText4>
            </button>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={removeTooltipId}>{t('requires_admin')}</Tooltip>
              }
            >
              <span
                className="ml-xs"
                tabIndex={0}
                aria-label={`${t('remove')} ${name}: ${t('requires_admin')}`}
              >
                <Text.BodyText4 tag="span" className="mb-0" color="grey50">
                  {t('remove')}
                </Text.BodyText4>
              </span>
            </OverlayTrigger>
          )}
        </Switch>
      </BodyCellContainer>
    </tr>
  )
}

export default Row
